import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { useI18n } from '../../i18n';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import './AcceptInvitePage.css';

type InviteKind = 'guard' | 'client';

interface InvitePayload {
  id: string;
  email: string;
  full_name?: string | null;
  company_name?: string | null;
  client_contract_name?: string | null;
  expires_at?: string | null;
}

interface InviteRpcResult {
  success: boolean;
  invite: InvitePayload;
}

interface AcceptClientInviteResult {
  success: boolean;
  userId: string;
  email: string;
}

async function getFunctionErrorMessage(error: unknown): Promise<string | null> {
  const context = (error as { context?: Response | null })?.context;
  if (context) {
    try {
      const payload = await context.clone().json() as { error?: string; message?: string };
      return payload.error ?? payload.message ?? null;
    } catch {
      // Fall back to the SDK error below.
    }
  }

  return error instanceof Error ? error.message : null;
}

export default function AcceptInvitePage() {
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const inviteKind: InviteKind = searchParams.get('type') === 'client' ? 'client' : 'guard';

  const [invite, setInvite] = useState<InvitePayload | null>(null);
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const inviteTitle = useMemo(() => (
    inviteKind === 'client' ? t('Activate Client Portal') : t('Accept Invite')
  ), [inviteKind, t]);

  useEffect(() => {
    if (!token) {
      setError(t('No invite token provided'));
      setIsLoading(false);
      return;
    }

    const rpcName = inviteKind === 'client' ? 'get_client_invite_by_token' : 'get_invite_by_token';

    void (async () => {
      try {
        const { data, error: rpcError } = await supabase.rpc(rpcName, { p_token: token });
        if (rpcError) throw rpcError;
        const payload = data as InviteRpcResult;
        if (!payload?.success || !payload.invite) {
          throw new Error(t('Invalid or expired invite'));
        }
        setInvite(payload.invite);
        setFullName(payload.invite.full_name ?? '');
      } catch (err) {
        setError((err as Error).message || t('Failed to load invite'));
      } finally {
        setIsLoading(false);
      }
    })();
  }, [inviteKind, token, t]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!invite || !token) return;

    if (inviteKind === 'client' && !fullName.trim()) {
      setError(t('Please confirm the client contact name'));
      return;
    }

    if (password.length < 8) {
      setError(t('Password must be at least 8 characters'));
      return;
    }
    if (!/[A-Z]/.test(password)) {
      setError(t('Password must contain at least one uppercase letter'));
      return;
    }
    if (!/[a-z]/.test(password)) {
      setError(t('Password must contain at least one lowercase letter'));
      return;
    }
    if (!/[0-9]/.test(password)) {
      setError(t('Password must contain at least one number'));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('Passwords do not match'));
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      if (inviteKind === 'client') {
        const { data: activationData, error: activationError } = await supabase.functions.invoke('accept-client-invite', {
          body: {
            token,
            fullName: fullName.trim(),
            password,
          },
        });

        if (activationError) {
          throw new Error(await getFunctionErrorMessage(activationError) ?? t('Failed to activate invite'));
        }

        const activation = activationData as AcceptClientInviteResult;
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email: activation.email || invite.email,
          password,
        });

        if (signInError) {
          setSuccess(true);
          return;
        }

        navigate('/', { replace: true });
        return;
      }

      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: invite.email,
        password,
      });

      if (authError) throw authError;

      const userId = authData.user?.id ?? authData.session?.user.id;
      if (!userId) {
        throw new Error(t('Account creation did not return a user. Check Supabase email confirmation settings.'));
      }

      const { error: acceptError } = await supabase.rpc('accept_guard_invite', {
        p_invite_token: token,
        p_user_id: userId,
      });
      if (acceptError) throw acceptError;

      setSuccess(true);
    } catch (err) {
      setError((err as Error).message || t('Failed to accept invite'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="auth-layout accept-invite__loading">
        <div className="spinner" />
      </div>
    );
  }

  if (success) {
    return (
      <div className="auth-layout">
        <div className="auth-layout__lang">
          <LanguageSwitcher />
        </div>
        <div className="auth-card accept-invite__success-card">
          <h1 className="auth-card__title">
            {inviteKind === 'client' ? t('Client Portal Ready') : t('Account Created')}
          </h1>
          <p className="accept-invite__success-text">
            {inviteKind === 'client'
              ? t('Your client portal credentials have been created. Continue to sign in and view your sites and schedules.')
              : t('Your account has been created successfully. You can now sign in.')}
          </p>
          <button
            onClick={() => navigate('/')}
            className="btn btn--primary accept-invite__success-btn"
          >
            {inviteKind === 'client' ? t('Go to Portal') : t('Go to Login')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-layout">
      <div className="auth-layout__lang">
        <LanguageSwitcher />
      </div>
      <form onSubmit={handleSubmit} className="auth-card">
        <h1 className="auth-card__title">{inviteTitle}</h1>
        {error && <div className="msg--error">{error}</div>}

        {invite && (
          <>
            <p className="accept-invite__subtitle">
              {inviteKind === 'client'
                ? t('You were invited by {company} to access {contract}.', {
                    company: invite.company_name ?? 'OdinsEye',
                    contract: invite.client_contract_name ?? t('your client portal'),
                  })
                : t('Welcome, {name}.', { name: invite.full_name ?? invite.email })}
            </p>
            <p className="accept-invite__subtitle accept-invite__subtitle--spaced">
              {inviteKind === 'client'
                ? t('Confirm your details and create a password to activate the portal.')
                : t('Create a password to set up your account.')}
            </p>

            <div className="form__group">
              <label className="form__label">{t('Email')}</label>
              <input
                type="email"
                value={invite.email}
                readOnly
                className="form__input form__input--readonly"
              />
            </div>

            {inviteKind === 'client' && (
              <div className="form__group">
                <label className="form__label">{t('Client contact name')}</label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="form__input"
                />
              </div>
            )}

            <div className="form__group">
              <label className="form__label">{t('Password')}</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="form__input"
              />
            </div>

            <div className="form__group accept-invite__form-group--last">
              <label className="form__label">{t('Confirm Password')}</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="form__input"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="btn btn--primary btn--full-width"
            >
              {isSubmitting
                ? (inviteKind === 'client' ? t('Activating Portal...') : t('Creating Account...'))
                : (inviteKind === 'client' ? t('Activate Portal') : t('Create Account'))}
            </button>
          </>
        )}
      </form>
    </div>
  );
}
